'use strict';

/**
 * Telemetry for derived names the answer store can never hold.
 *
 * The only part of the adapter that reads ambient state (`page_state`,
 * `window.newrelic`), kept here so `./variables.js` stays pure and `./index.js`
 * stays about reading values. Reports; never filters, and never throws.
 */

// Membership index for `page_state.allvars`, cached on array identity exactly
// as `answer-sync.js` does it: the array is assigned in one place
// (`globals/page.js` `discover_variables`) and never mutated, so a new question
// brings a new array and the index rebuilds. Prototype-free, so a variable
// named `constructor` cannot read as present.
var cachedAllvars = null;
var cachedAllvarsIndex = null;

function allvarsIndex() {
  var arr =
    typeof page_state !== 'undefined' && page_state ? page_state.allvars : null;
  if (!arr || !arr.length) {
    cachedAllvars = null;
    cachedAllvarsIndex = null;
    return null;
  }
  if (arr !== cachedAllvars) {
    cachedAllvars = arr;
    cachedAllvarsIndex = Object.create(null);
    for (var i = 0; i < arr.length; i++) {
      cachedAllvarsIndex[arr[i]] = true;
    }
  }
  return cachedAllvarsIndex;
}

// One report per page, like `answer-sync.js` and `shadow-compare.js`. The
// signal is "this page derives names the store can never hold"; one event
// carries it, and a widget can call in on every keystroke.
var lastUndeclaredReportedFor = null;

/**
 * Report any derived name that is not a declared variable for this page.
 *
 * The store only ever holds keys present in `page_state.allvars` — the
 * server's list, applied as a gate in `answer-sync.js` `answerKeyFor`. The
 * adapter derives its names independently, from the question object
 * (`./variables.js`), so the two can disagree. When they do, the name is absent
 * from the store forever and reads as `undefined`, which is indistinguishable
 * from "nobody answered it" — so a required-satisfied rule built on it would
 * block a respondent who did answer, silently.
 *
 * Reporting rather than filtering is deliberate. Dropping the name would hide
 * the derivation bug and make the snapshot depend on ambient page state, so the
 * same question object would yield different key sets on different pages.
 *
 * This is the mirror image of `answer-seed.js` `unseededVariables`, and the two
 * do not overlap: that one reports declared variables the seed could not
 * produce, this one reports derived names that were never declared. Both take
 * `res.variables` as the oracle — it arrives here as `page_state.allvars`.
 *
 * No-ops when `allvars` is absent or empty, which covers tests and early boot.
 * Names only ever leave here as names; values are never reported.
 *
 * @param {string[]} names Derived variable names.
 */
export function reportUndeclared(names) {
  try {
    var index = allvarsIndex();
    if (index === null) {
      return;
    }
    var undeclared = [];
    for (var i = 0; i < names.length; i++) {
      if (index[names[i]] !== true) {
        undeclared.push(names[i]);
      }
    }
    if (!undeclared.length) {
      return;
    }
    var page = page_state.this_page || '';
    if (lastUndeclaredReportedFor === page) {
      return;
    }
    lastUndeclaredReportedFor = page;
    // `noticeError` directly rather than `report.logError`, which takes no
    // attributes — the same reason `Navigation/shadow-compare.js` does.
    if (typeof window.newrelic === 'undefined') {
      return;
    }
    var message = 'answers-adapter derived undeclared variables';
    window.newrelic.noticeError(new Error(message), {
      errorMessage: message,
      page: page,
      adapterUndeclared: true,
      undeclaredCount: undeclared.length,
      // Names only, capped. Answer values never leave this module.
      undeclaredVars: undeclared.slice(0, 20).join(','),
    });
  } catch (e) {
    // A broken check must never break a caller mid-render.
  }
}
